import { useEffect, useState } from "react";
import { GroupCreate } from "./component/group-create";
import { groupApi } from "./group-api";
import { Group } from "./types";
import { toast } from "react-toastify";
import PermissionGuard from "../../components/PermissionGuard";

export const GroupList = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [isShowTeamCreate, setShowTeamCreate] = useState(false);
  const [isShowTeamEdit, setShowTeamEdit] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = (await groupApi.getAllGroups()) as any;
      setGroups(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      toast.error("Failed to fetch groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  function handleGroupEdit(group: Group): void {
    setSelectedGroup(group);
    setShowTeamEdit(true);
    setShowTeamCreate(false);
  }

  function handleCreateGroupClose(): void {
    setShowTeamEdit(false);
    setShowTeamCreate(false);
    fetchGroups();
  }

  const handleGroupDelete = async (group: Group) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete Group ${group.name} with ${group.members?.length} member(s)?`
    );
    if (isConfirmed) {
      try {
        await groupApi.deleteGroup(group.id!);
        toast.success("Group deleted successfully");
        fetchGroups();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An error occurred";
        setError(errorMessage);
        toast.error("Failed to delete group");
      }
    }
  };

  const filteredGroups = groups.filter(
    (group) =>
      !searchQuery ||
      group.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      group.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Group Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Organize and manage your groups efficiently
              </p>
            </div>
            <PermissionGuard permission={["group.create", "group.manage"]}>
              <button
                onClick={() => setShowTeamCreate(true)}
                className="btn-primary flex items-center gap-2 px-6 py-3 shadow-lg"
              >
                <i className="fa-solid fa-plus"></i>
                <span>Create New Group</span>
              </button>
            </PermissionGuard>
          </div>

          {/* Filters Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 p-6 mb-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex-1 w-full">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 pr-10 bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                    placeholder="Search groups..."
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                    <i className="fa-solid fa-search"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation text-red-600 dark:text-red-400"></i>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
              <p className="text-gray-600 dark:text-gray-300">Loading groups...</p>
            </div>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <i className="fa-solid fa-users text-3xl text-gray-400 dark:text-gray-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                No groups found
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Get started by creating your first group
              </p>
              <PermissionGuard permission={["group.create", "group.manage"]}>
                <button
                  onClick={() => setShowTeamCreate(true)}
                  className="btn-primary mt-4 flex items-center gap-2 px-6 py-3"
                >
                  <i className="fa-solid fa-plus"></i>
                  <span>Create New Group</span>
                </button>
              </PermissionGuard>
            </div>
          </div>
        ) : (
          /* Groups Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGroups.map((group) => (
              <div
                key={group.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-200 overflow-hidden"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {group.name}
                      </h3>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        <i className="fa-solid fa-circle text-[6px] mr-2"></i>
                        Active
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <PermissionGuard permission={["group.update", "group.manage"]}>
                        <button
                          onClick={() => handleGroupEdit(group)}
                          className="p-2 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Edit group"
                        >
                          <i className="fa-solid fa-edit"></i>
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission={["group.delete", "group.manage"]}>
                        <button
                          onClick={() => handleGroupDelete(group)}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete group"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </PermissionGuard>
                    </div>
                  </div>

                  {/* Description */}
                  {group.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                      {group.description}
                    </p>
                  )}

                  {/* Members Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                        Members
                      </div>
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-semibold">
                        {group.members?.length || 0}
                      </span>
                    </div>
                    {group.members && group.members.length > 0 ? (
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-2">
                          {group.members.slice(0, 4).map((member, index) => (
                            <div
                              key={member.id || index}
                              className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-800 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-semibold"
                              title={member.name}
                            >
                              {member.name?.charAt(0).toUpperCase() || "M"}
                            </div>
                          ))}
                        </div>
                        {group.members.length > 4 && (
                          <span className="text-xs text-gray-500 dark:text-gray-300 font-medium">
                            +{group.members.length - 4} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                        No members yet
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {isShowTeamCreate && (
        <GroupCreate onClose={() => handleCreateGroupClose()} isCreate={true} />
      )}
      {isShowTeamEdit && selectedGroup != null && (
        <GroupCreate
          onClose={() => handleCreateGroupClose()}
          group={selectedGroup}
          isCreate={false}
        />
      )}
    </div>
  );
};
